/**
 * Serialization utilities for localStorage
 *
 * These utilities enable serialization and deserialization of data structures (not classes)
 * for storage within localStorage. Standard JSON.stringify/parse cannot handle complex data types
 * like Sets and Maps, which these utilities are designed to properly preserve.
 *
 * Supported data types:
 * - Primitive values (strings, numbers, boolean, null)
 * - Arrays and Objects
 * - Sets
 * - Maps
 * - Nested combinations of the above
 */

interface SerializedSet {
  dataType: 'Set'
  value: unknown[]
}

interface SerializedMap {
  dataType: 'Map'
  value: [unknown, unknown][]
}

type SerializedData = SerializedSet | SerializedMap

const jsonStringify = <T>(dataWithMaps: T) =>
  JSON.stringify(dataWithMaps, (key, value) => {
    if (value instanceof Map) {
      return {
        dataType: 'Map',
        value: Array.from(value.entries()),
      } as SerializedMap
    }
    if (value instanceof Set) {
      return { dataType: 'Set', value: Array.from(value) } as SerializedSet
    }
    return value as unknown
  })

const jsonParse = <T>(value: string) =>
  JSON.parse(value, (_key, value) => {
    if (value && typeof value === 'object') {
      const v = value as SerializedData

      if (v.dataType === 'Map') return new Map(v.value)
      if (v.dataType === 'Set') return new Set(v.value)

      return v
    }
    return value as unknown
  }) as T

export const serialize = <T>(
  value: T,
  options = { onError: (_error: Error): void => undefined },
): string => {
  try {
    return jsonStringify(value)
  } catch (error) {
    const serializeError = error as Error
    options.onError(serializeError)
    return ''
  }
}

export const deserialize = <T>(
  value: string,
  options = { onError: (_error: Error): void => undefined },
): T | undefined => {
  try {
    return jsonParse<T>(value)
  } catch (error) {
    const deserializeError = error as Error
    options.onError(deserializeError)
    return undefined
  }
}
